"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv, type VariantProps } from "../utils/tv";
import { recursiveCloneChildren } from "../utils/recursive-clone-children";
import type { PolymorphicComponentProps } from "../utils/polymorphic";

const BUTTON_ROOT_NAME = "ButtonRoot";
const BUTTON_ICON_NAME = "ButtonIcon";

/**
 * AlignUI Button Variants
 * Supports compound component pattern with Button.Root and Button.Icon
 */
export const buttonVariants = tv({
  slots: {
    root: [
      // Base styles
      "group/button relative inline-flex items-center justify-center whitespace-nowrap outline-none",
      "transition duration-200 ease-out",
      "focus:outline-none",
      // Disabled state
      "disabled:pointer-events-none disabled:bg-bg-weak-50 disabled:text-text-disabled-300 disabled:shadow-none",
    ],
    icon: "flex size-5 shrink-0 items-center justify-center",
  },
  variants: {
    variant: {
      primary: {},
      neutral: {},
      error: {},
    },
    mode: {
      filled: {},
      stroke: {
        root: "bg-bg-white-0 ring-1 ring-inset",
      },
      lighter: {
        root: "ring-1 ring-inset ring-transparent",
      },
      ghost: {
        root: "bg-transparent",
      },
    },
    size: {
      medium: {
        root: "h-10 gap-3 rounded-10 px-3.5 text-label-sm",
        icon: "-mx-1",
      },
      small: {
        root: "h-9 gap-3 rounded-8 px-3 text-label-sm",
        icon: "-mx-1",
      },
      xsmall: {
        root: "h-8 gap-2.5 rounded-8 px-2.5 text-label-sm",
        icon: "-mx-0.5",
      },
      xxsmall: {
        root: "h-7 gap-2 rounded-8 px-2 text-label-xs",
        icon: "-mx-0.5",
      },
    },
  },
  compoundVariants: [
    // Primary + Filled
    {
      variant: "primary",
      mode: "filled",
      class: {
        root: [
          "bg-primary-base text-static-white",
          "hover:bg-primary-darker",
          "active:bg-primary-dark",
          "focus-visible:shadow-button-primary-focus",
        ],
      },
    },
    // Primary + Stroke
    {
      variant: "primary",
      mode: "stroke",
      class: {
        root: [
          "text-primary-base ring-primary-base",
          "hover:bg-primary-alpha-10",
          "active:bg-primary-alpha-16",
          "focus-visible:shadow-button-primary-focus",
        ],
      },
    },
    // Primary + Lighter
    {
      variant: "primary",
      mode: "lighter",
      class: {
        root: [
          "bg-primary-alpha-10 text-primary-base",
          "hover:bg-primary-alpha-16",
          "active:bg-primary-alpha-24",
          "focus-visible:shadow-button-primary-focus",
        ],
      },
    },
    // Primary + Ghost
    {
      variant: "primary",
      mode: "ghost",
      class: {
        root: [
          "text-primary-base",
          "hover:bg-primary-alpha-10",
          "active:bg-primary-alpha-16",
          "focus-visible:shadow-button-primary-focus",
        ],
      },
    },
    // Neutral + Filled
    {
      variant: "neutral",
      mode: "filled",
      class: {
        root: [
          "bg-bg-strong-950 text-text-white-0",
          "hover:bg-gray-800",
          "active:bg-gray-700",
          "focus-visible:shadow-button-important-focus",
        ],
      },
    },
    // Neutral + Stroke
    {
      variant: "neutral",
      mode: "stroke",
      class: {
        root: [
          "text-text-strong-950 ring-stroke-sub-300",
          "hover:bg-bg-weak-50 hover:ring-stroke-strong-950",
          "active:bg-bg-soft-200",
          "focus-visible:shadow-button-important-focus",
        ],
      },
    },
    // Neutral + Lighter
    {
      variant: "neutral",
      mode: "lighter",
      class: {
        root: [
          "bg-bg-weak-50 text-text-strong-950",
          "hover:bg-bg-soft-200",
          "active:bg-bg-sub-300",
          "focus-visible:shadow-button-important-focus",
        ],
      },
    },
    // Neutral + Ghost
    {
      variant: "neutral",
      mode: "ghost",
      class: {
        root: [
          "text-text-strong-950",
          "hover:bg-bg-weak-50",
          "active:bg-bg-soft-200",
          "focus-visible:shadow-button-important-focus",
        ],
      },
    },
    // Error + Filled
    {
      variant: "error",
      mode: "filled",
      class: {
        root: [
          "bg-error-base text-static-white",
          "hover:bg-error-darker",
          "active:bg-error-dark",
          "focus-visible:shadow-button-error-focus",
        ],
      },
    },
    // Error + Stroke
    {
      variant: "error",
      mode: "stroke",
      class: {
        root: [
          "text-error-base ring-error-base",
          "hover:bg-error-lighter",
          "active:bg-error-light",
          "focus-visible:shadow-button-error-focus",
        ],
      },
    },
    // Error + Lighter
    {
      variant: "error",
      mode: "lighter",
      class: {
        root: [
          "bg-error-lighter text-error-base",
          "hover:bg-error-light",
          "active:bg-red-200",
          "focus-visible:shadow-button-error-focus",
        ],
      },
    },
    // Error + Ghost
    {
      variant: "error",
      mode: "ghost",
      class: {
        root: [
          "text-error-base",
          "hover:bg-error-lighter",
          "active:bg-error-light",
          "focus-visible:shadow-button-error-focus",
        ],
      },
    },
  ],
  defaultVariants: {
    variant: "primary",
    mode: "filled",
    size: "medium",
  },
});

type ButtonSharedProps = VariantProps<typeof buttonVariants>;

type ButtonRootProps = VariantProps<typeof buttonVariants> &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean;
  };

const ButtonRoot = React.forwardRef<HTMLButtonElement, ButtonRootProps>(
  ({ children, variant, mode, size, asChild, className, disabled, ...rest }, forwardedRef) => {
    const uniqueId = React.useId();
    const Component = asChild ? Slot : "button";
    const { root } = buttonVariants({ variant, mode, size });

    const sharedProps: ButtonSharedProps = { variant, mode, size };

    const extendedChildren = recursiveCloneChildren(
      children as React.ReactElement[],
      sharedProps,
      [BUTTON_ICON_NAME],
      uniqueId,
      asChild
    );

    return (
      <Component
        ref={forwardedRef}
        className={root({ class: className })}
        disabled={disabled}
        {...rest}
      >
        {extendedChildren}
      </Component>
    );
  }
);
ButtonRoot.displayName = BUTTON_ROOT_NAME;

function ButtonIcon<T extends React.ElementType>({
  className,
  as,
  variant,
  mode,
  size,
  ...rest
}: PolymorphicComponentProps<T, ButtonSharedProps>) {
  const Component = as || "div";
  const { icon } = buttonVariants({ variant, mode, size });

  return <Component className={icon({ class: className })} {...rest} />;
}
ButtonIcon.displayName = BUTTON_ICON_NAME;

export { ButtonRoot as Root, ButtonIcon as Icon };
